"use client";

import type { AuthChangeEvent } from "@supabase/supabase-js";

import { useCallback, useEffect, useState } from "react";

import type { LibraryServerActions } from "@/lib/library/store";
import { useAuth, useAuthEvent } from "@/lib/hooks/useAuth";
import { LibraryContext } from "@/lib/hooks/useLibrary";
import { createLibraryChannel } from "@/lib/library/channel";
import { createLibraryStorage } from "@/lib/library/storage";
import { LibraryStore } from "@/lib/library/store";
import {
  addToWatchlist,
  getLibrary,
  markAsWatched,
  rateMovie,
  removeFromWatchlist,
  removeRating,
  restoreLibraryEntry,
  unmarkAsWatched,
} from "@/lib/supabase/actions";

const serverActions: LibraryServerActions = {
  getLibrary,
  addToWatchlist,
  removeFromWatchlist,
  markAsWatched,
  unmarkAsWatched,
  rateMovie,
  removeRating,
  restoreLibraryEntry,
};

export interface LibraryProviderProps {
  children: React.ReactNode;
  /** Override the server actions (tests). */
  actions?: LibraryServerActions;
}

/**
 * Owns the app-wide library store and keeps it in step with auth:
 * loads the signed-in user's library, clears everything on sign-out and
 * revalidates after token refreshes, on focus and when tabs change it.
 */
export function LibraryProvider({ children, actions }: LibraryProviderProps) {
  const [store] = useState(
    () =>
      new LibraryStore({
        actions: actions ?? serverActions,
        storage: typeof window === "undefined" ? null : createLibraryStorage(),
        createChannel: createLibraryChannel,
      }),
  );
  const { status, user } = useAuth();
  const userId = status === "loading" ? undefined : (user?.id ?? null);

  useEffect(() => store.connect(), [store]);

  useEffect(() => {
    if (userId !== undefined) store.setUser(userId);
  }, [store, userId]);

  const onAuthEvent = useCallback(
    (event: AuthChangeEvent) => {
      if (event === "SIGNED_OUT") store.setUser(null);
      if (event === "TOKEN_REFRESHED") void store.revalidate();
    },
    [store],
  );

  useAuthEvent(onAuthEvent);

  return (
    <LibraryContext.Provider value={store}>{children}</LibraryContext.Provider>
  );
}
